import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { formatDate } from '../utils/helpers';
import SocialPostForm from '../components/forms/SocialPostForm';
import BaseModal from '../components/modals/BaseModal';
import { useCRM } from '../context/CRMContext';

const SocialMedia = () => {
  const { integrations } = useCRM();

  // Find connected social accounts dynamically
  const connectedSocials = integrations.filter(i => i.category === 'Social Media' && i.connected);
  const channels = ['Alle', ...connectedSocials.map(s => s.name)];

  const [posts] = useState([
    { 
      post_id: 'post-1', 
      user_id: 'user-1', 
      project_id: 'proj-1', 
      channel: 'LinkedIn Personal Profile', 
      content: 'Ny AI-teknologi revolusjonerer kundeservice! 🚀 Vår løsning reduserer responstid med 80% og øker kundetilfredshet dramatisk. #AI #kundeservice', 
      status: 'Publisert', 
      stats: { views: 1250, clicks: 89, likes: 45, comments: 12, shares: 8 } 
    },
    { 
      post_id: 'post-2', 
      user_id: 'user-1', 
      project_id: 'proj-1', 
      channel: 'LinkedIn Company Page', 
      content: 'Vi søker nye talenter! Bli med på reisen vår og form fremtidens CRM-løsninger. Sjekk ut våre ledige stillinger på nettsiden. 💼✨ #hiring #tech #vekst', 
      status: 'Planlagt', 
      stats: { views: 0, clicks: 0, likes: 0, comments: 0, shares: 0 } 
    },
    { 
      post_id: 'post-3', 
      user_id: 'user-1', 
      project_id: 'proj-1', 
      channel: 'Facebook Page', 
      content: 'Takk til alle som besøkte standen vår på tech-messen i går! Det var fantastisk å se så mye engasjement. 🎉 Sjekk ut høydepunktene i videoen under.', 
      status: 'Publisert', 
      stats: { views: 3400, clicks: 210, likes: 156, comments: 34, shares: 12 } 
    }
  ]);

  const [selectedChannel, setSelectedChannel] = useState('Alle');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);

  const filteredPosts = selectedChannel === 'Alle' 
    ? posts 
    : posts.filter(post => post.channel === selectedChannel);

  const handleNew = () => {
    if (connectedSocials.length === 0) {
      alert("Du må koble til en sosiale medier-konto under Integrasjoner først.");
      return;
    }
    setEditingPost(null);
    setIsModalOpen(true);
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Sosiale Medier</h1>
          <p className="text-slate-500 font-medium">Planlegg og publiser innhold på tvers av plattformer.</p>
        </div>
        <button 
          onClick={handleNew}
          className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all shadow-lg"
        >
          <SafeIcon icon="➕" className="w-4 h-4 mr-2" />
          Nytt innlegg
        </button>
      </div>

      {connectedSocials.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 p-4 rounded-xl flex items-center gap-3 text-amber-800 text-sm font-bold">
          <SafeIcon icon="⚠️" className="text-xl" />
          Du har ingen sosiale kanaler koblet til. Gå til Integrasjoner for å koble til.
        </div>
      )}

      {connectedSocials.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-2 custom-scrollbar">
          {channels.map((channel) => (
            <button 
              key={channel}
              onClick={() => setSelectedChannel(channel)}
              className={`px-4 py-2 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${selectedChannel === channel ? 'bg-slate-900 text-white shadow-md' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}
            >
              {channel}
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {filteredPosts.map((post) => (
          <motion.div 
            key={post.post_id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex justify-between items-start mb-4">
              <span className="px-2 py-1 bg-blue-50 text-blue-700 text-[10px] font-bold rounded border border-blue-100">{post.channel}</span>
              <span className={`px-2 py-1 text-[10px] font-bold rounded border ${post.status === 'Publisert' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-amber-50 text-amber-700 border-amber-100'}`}>
                {post.status}
              </span>
            </div>
            <p className="text-sm text-slate-700 mb-6 leading-relaxed bg-slate-50 p-4 rounded-xl border border-slate-100">{post.content}</p>
            
            {post.status === 'Publisert' ? (
              <div className="grid grid-cols-4 gap-2 pt-4 border-t border-slate-100 text-center">
                <div><p className="text-xs font-bold text-slate-900">{post.stats.views}</p><p className="text-[9px] text-slate-400 uppercase tracking-widest mt-1">Visninger</p></div>
                <div><p className="text-xs font-bold text-slate-900">{post.stats.likes}</p><p className="text-[9px] text-slate-400 uppercase tracking-widest mt-1">Likes</p></div>
                <div><p className="text-xs font-bold text-slate-900">{post.stats.comments}</p><p className="text-[9px] text-slate-400 uppercase tracking-widest mt-1">Komm.</p></div>
                <div><p className="text-xs font-bold text-slate-900">{post.stats.clicks}</p><p className="text-[9px] text-slate-400 uppercase tracking-widest mt-1">Klikk</p></div>
              </div>
            ) : (
               <div className="pt-4 border-t border-slate-100 text-center">
                <p className="text-xs text-slate-400 font-bold uppercase tracking-widest">Planlagt for publisering</p>
               </div>
            )}
          </motion.div>
        ))}
        
        {filteredPosts.length === 0 && (
          <div className="col-span-full py-12 text-center border-2 border-dashed border-slate-200 rounded-2xl">
            <SafeIcon icon="🤷" className="text-4xl text-slate-300 mb-3" />
            <p className="text-slate-500 font-bold text-sm">Ingen innlegg funnet for denne kanalen.</p>
          </div>
        )}
      </div>

      <BaseModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Nytt innlegg">
        <SocialPostForm post={editingPost} onClose={() => setIsModalOpen(false)} connectedChannels={connectedSocials} />
      </BaseModal>
    </div>
  );
};

export default SocialMedia;